import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/src/components/ui/button.tsx";
import { Badge } from "@/src/components/ui/badge.tsx";
import { handleAnalyticsClick } from "@/src/utils/analytics";
import { API_ENDPOINTS } from "@/src/config/api.js";


export default function FeaturedCarousel() {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [featuredItems, setFeaturedItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    useEffect(() => {
        fetch(API_ENDPOINTS.activity.featuredToday)
            .then(response => response.json())
            .then(data => {
                // Filter out activities where body.homeFeature is false
                const filteredActivities = data.activities.filter(item =>
                    item.body?.homeFeature !== false
                );
                const formattedItems = filteredActivities.slice(0, 12).filter(item =>
                    item.body?.homeFeature !== false
                ).map(item => {
                    const itemType = item.type === 'general' && item.title.toLowerCase().includes('interview')
                        ? 'interview'
                        : item.type === 'general'
                            ? 'Dream Chimney'
                            : item.type;
                    // const imageUrl = item.title.toLowerCase().includes('interview')
                    //    ? item.img_url.replace('.jpg', '-wide.jpg') : item.img_url;
                    const imageUrl = item.img_url?.startsWith('http')
                        ? item.img_url
                        : `https://www.dreamchimney.com${item.img_url}`;

                    item.type = itemType;

                    return ({
                        id: item.id,
                        type: item.type === 'general' && item.title.toLowerCase().includes('interview')
                            ? 'interview'
                            : item.type.replace('-', ' '),
                        title: item.title,
                        description: item.body.teaser || item.body.subtitle,
                        body: item.body,
                        image: imageUrl,
                        date: formatDate(item.date.date),
                        duration: item.body.label || item.body.title || item.body.subtitle,
                        badge: getBadgeColor(item.type),
                        link: item.link_url
                    })
                });
                setFeaturedItems(formattedItems);
            })
            .catch(error => console.error("Error fetching featured items:", error));
    }, []);

    const getTitle = (item) => {
        const titleMap = {
            'motw': `${item.title} by ${item?.body?.dj}`,
            'totd': item.title,
            'interview': item.title,
            'general': item.title
        };
        return titleMap[item.type?.toLowerCase()] || item.title;
    };

    const getTypeDisplay = (type) => {
        const typeMap = {
            'totd promo': 'DC Promo Tracks',
            'totd': 'Track of the Day',
            'motw': 'Mix of the Week',
            'interview': 'Interview',
            'general': 'General'
        };
        return typeMap[type.toLowerCase()] || `${type}`;
    };

    const getBadgeColor = (type) => {
        const colors = {
            'motw': 'bg-orange-500',
            'totd': 'bg-green-600',
            'totd-promo': 'bg-purple-600',
            'interview': 'bg-interviews-blue',
            'default': 'bg-indigo-600'
        };
        return colors[type.toLowerCase()] || colors.default;
    };

    const totalSlides = Math.ceil(featuredItems.length / 3);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    };

    const goToSlide = (index) => {
        setCurrentSlide(index);
    };

    const carouselRef = useRef(null); // Create a ref for the carousel container

    useEffect(() => {
        // This code runs after the component mounts and whenever its dependencies change

        const trackCarouselClick = (event) => {
            // Find the closest carousel item ancestor of the clicked element
            const clickedItem = event.target.closest('.home-featured-carousel-item');

            if (clickedItem) {
                // Extract relevant information from the clicked item's attributes
                const itemId = clickedItem.dataset.itemId || 'unknown';
                const itemTitle = clickedItem.dataset.itemTitle || clickedItem.innerText.trim() || 'unknown';
                const carouselName = 'home_feat_carousel'; // Specific to your carousel

                // Find the position of the clicked item in the carousel
                const carouselItems = carouselRef.current?.querySelectorAll('.home-featured-carousel-item');
                const clickedIndex = Array.from(carouselItems || []).indexOf(clickedItem);
                const carouselPosition = clickedIndex !== -1 ? clickedIndex + 1 : 'unknown';

                const eventParams = {
                    eventCategory: 'Carousel',
                    eventAction: 'Click',
                    eventLabel: itemTitle,
                    itemId: itemId,
                    carouselPosition: carouselPosition,
                    // Add any other relevant data you want to track
                };

                handleAnalyticsClick('home_featured_carousel', eventParams, event);
            }
        };

        // Attach the event listener to the carousel container
        // Using event delegation: listen on the parent and check the target
        if (carouselRef.current) {
            carouselRef.current.addEventListener('click', trackCarouselClick);
        }

        // Cleanup function: remove the event listener when the component unmounts
        return () => {
            if (carouselRef.current) {
                carouselRef.current.removeEventListener('click', trackCarouselClick);
            }
        };
    }, []); // Empty dependency array means this effect runs once after the initial render


    return (
        <section className="relative h-[60vh] overflow-hidden">
            <div className="absolute inset-0"></div>

            <div className="container mx-auto px-4 py-6 h-full relative z-10">
                <div className="relative h-full overflow-hidden">
                    <div
                        ref={carouselRef}
                        className="flex home-featured-carousel transition-transform duration-500 ease-in-out h-full"
                        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                    >
                        {featuredItems.map((item) => (
                            <div key={item.id} data-item-id={`carousel-${item.id}`} data-item-title={item.title} className="home-featured-carousel-item px-3">
                                <a href={item.link} className="relative h-full group cursor-pointer block">
                                    <div className="relative h-full overflow-hidden rounded-lg">
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent"></div>

                                        <div className="absolute bottom-4 left-4 right-4" title={item.description}>
                                            <h3 className="text-lg text-white font-bold mb-2 line-clamp-2">{getTitle(item)}</h3>
                                            <p className="text-sm text-gray-300 mb-3 line-clamp-2">{item.description}</p>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2 text-xs text-gray-400">
                                                    <span>{item.date}</span>
                                                    <span>•</span>
                                                    <span>{item.duration && item.duration.length > 20 ? item.duration.substring(0, 20) + '...' : item.duration}</span>
                                                </div>
                                                <Badge className={`${item.badge} text-white hover:${item.badge}`} style={{ fontSize: item.duration.length > 16 ? '0.5rem' : null }}>{getTypeDisplay(item.type)}</Badge>
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            </div>
                        ))}
                    </div>

                    <Button
                        variant="ghost"
                        size="icon"
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white"
                        onClick={prevSlide}
                    >
                        <ChevronLeft className="h-6 w-6" />
                    </Button>

                    <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white"
                        onClick={nextSlide}
                    >
                        <ChevronRight className="h-6 w-6" />
                    </Button>
                </div>
            </div>

            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 z-20 mb-2">
                <div className="flex space-x-2">
                    {Array.from({ length: totalSlides }).map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`w-2 h-2 rounded-full transition-all duration-200 ${index === currentSlide
                                ? "bg-neutral-800 scale-110"
                                : "bg-neutral-600 hover:bg-neutral-700"
                                }`}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}